import * as controllers from 'controllers'
import { ClusterShipperDto } from 'dtos/clusterShipperDto'
import { useLoadingState } from 'hooks'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import ClusterShippersAddEditDialog from './ClusterShippersAddEditDialog'
import { enqueueSnackbar } from 'notistack'
import { Button, Card, CardContent, Grid } from '@mui/material'
import ClusterShippersDetailsCard from './ClusterShippersDetailsCard'
import { Edit, PictureAsPdf } from '@mui/icons-material'
import ClusterShippersManageVehiclesListCard from './ClusterShippersManageVehiclesListCard'
import ClusterShippersThirdPartyConversionDeliveryPhotoCard from './ClusterShippersThirdPartyConversionDeliveryPhotoCard'
import { useLayout } from 'context'
import { ClusterShipperThirdPartyConversionDeliveryDto } from 'dtos'
import PDFObjectBase64 from 'components/PDFObjectBase64'
import { LoadingButton } from '@mui/lab'

export interface ClusterShippersThirdPartyConversionDeliveryPhotoCardProps {
  thirdParyClusterConversionDeliveryPhoto: ClusterShipperThirdPartyConversionDeliveryDto
  setThirdPartyClusterConversionDeliveryPhoto: React.Dispatch<
    React.SetStateAction<ClusterShipperThirdPartyConversionDeliveryDto>
  >
}

export default function ClusterShippersDetails() {
  const { clusterShipperGuid } = useParams()
  const layout = useLayout()

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingClusterShipper: false,
    isUpdatingClusterShipper: false
  })

  const [clusterShipper, setClusterShipper] = useState<ClusterShipperDto>(new ClusterShipperDto())
  const [clusterShipperAddEditDialogOpen, setClusterShipperAddEditDialogOpen] = useState<boolean>(false)
  const [base64, setBase64] = useState<string>('')
  const [isGeneratingClusterShipper, setIsGeneratingClusterShipper] = useState<boolean>(false)

  useEffect(() => {
    if (clusterShipperGuid && clusterShipperGuid.toLowerCase() !== 'undefined' && clusterShipperGuid.toLowerCase() !== 'new') {
      setLoadingState('isGettingClusterShipper', true)
      controllers
        .getClusterShipper(clusterShipperGuid)
        .then(response => {
          setClusterShipper(response.value)
        })
        .finally(() => {
          setLoadingState('isGettingClusterShipper', false)
        })
    }
  }, [clusterShipperGuid])

  useEffect(() => {
    layout.setTitle('Cluster Shipper Details')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingClusterShipper)
  }, [loadingState.isGettingClusterShipper])

  return (
    <>
      <ClusterShippersAddEditDialog
        initialValues={clusterShipper}
        isLoading={loadingState.isUpdatingClusterShipper}
        onClose={() => {
          setClusterShipperAddEditDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingClusterShipper', true)
          controllers
            .updateClusterShipper(values)
            .then(response => {
              setClusterShipper(response.value)
              setClusterShipperAddEditDialogOpen(false)
              enqueueSnackbar('Cluster Shipper Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setLoadingState('isUpdatingClusterShipper', false)
            })
        }}
        open={clusterShipperAddEditDialogOpen}
      />

      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <ClusterShippersDetailsCard clusterShipper={clusterShipper}>
            <LoadingButton
              disabled={clusterShipper.clusterShipperGuid === undefined}
              endIcon={<PictureAsPdf />}
              loading={isGeneratingClusterShipper}
              loadingPosition='end'
              onClick={() => {
                setIsGeneratingClusterShipper(true)
                controllers.getClusterShipperPdf(clusterShipper.clusterShipperGuid!).finally(() => {
                  setIsGeneratingClusterShipper(false)
                })
              }}
              variant='text'
            >
              GENERATE CLUSTER SHIPPER
            </LoadingButton>

            <Button
              endIcon={<Edit />}
              onClick={() => {
                setClusterShipperAddEditDialogOpen(true)
              }}
              variant='text'
            >
              EDIT
            </Button>
          </ClusterShippersDetailsCard>
        </Grid>

        <Grid item container xs={12} alignItems='flex-start'>
          <Grid item xs={6} md={7}>
            <ClusterShippersManageVehiclesListCard clusterShipper={clusterShipper} setClusterShipper={setClusterShipper} />
          </Grid>

          <Grid item xs={6} md={5}>
            <ClusterShippersThirdPartyConversionDeliveryPhotoCard clusterShipper={clusterShipper} />
          </Grid>
        </Grid>
      </Grid>

      {base64 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <PDFObjectBase64 base64={base64} />
            </CardContent>
          </Card>
        </Grid>
      )}
    </>
  )
}
